/**
 * AppShell layout component.
 *
 * Renders the persistent top navigation bar and wraps the route outlet
 * in a flex column layout. Used as the parent layout route in the router.
 * All authenticated routes render inside this shell via React Router's Outlet.
 *
 * Phase 3.1 (ADR-006 section 1): actor-type branching moved from this
 * component up to the router itself. buildRoutes registers admin routes
 * for staff and client routes for everyone else; mismatched direct URL
 * hits fall through to the SPA 404. AppShell is now a pure layout for
 * BOTH actor types and contains no actor-specific guards.
 *
 * Phase 3.1 Plan 03 (per ADR-006 section 3 + ADR-005):
 * AppShell mounts ImpersonationRedirect UNCONDITIONALLY and renders the
 * ImpersonationBanner conditionally on user.impersonating_as. The redirect
 * helper observes every user transition (rising / falling edge of
 * impersonating_as) and navigates to /now or /admin/fleet so the user
 * does not land on a deregistered route after a token swap.
 *
 * The requireAuthLoader on the layout route still handles the
 * unauthenticated case before this component renders.
 *
 * Modified: 2026-04-25
 */
import { Outlet } from 'react-router-dom';
import { TopNav } from './TopNav';
import { useAuth } from '@/features/auth/useAuth';
import {
  ImpersonationBanner,
  ImpersonationRedirect,
} from '@/features/impersonation';
import { shellRoot, shellMain } from './AppShell.css';

/**
 * AppShell component.
 *
 * Provides the persistent layout: optional banner + TopNav above, page
 * content (Outlet) below. ImpersonationRedirect is always mounted so it
 * can observe transitions even on routes where the banner is not shown.
 *
 * Renders nothing while AuthProvider is initializing to avoid a flash of
 * content before the auth state is known.
 */
export function AppShell() {
  const { user, status } = useAuth();

  // While the AuthProvider bootstraps (loading /v1/auth/me), render nothing
  // to avoid a flash of content before the auth state is known.
  if (status === 'initializing') {
    return null;
  }

  // The banner is shown only when the active user is impersonating a
  // client org. Plan 03's ImpersonationRedirect navigates to /now on the
  // rising edge so the banner appears under the client surface, never
  // under /admin/*.
  const showBanner = !!user?.impersonating_as;

  return (
    <div className={shellRoot}>
      <ImpersonationRedirect />
      {showBanner && <ImpersonationBanner />}
      <TopNav />
      <main className={shellMain}>
        <Outlet />
      </main>
    </div>
  );
}
